/**
 * Ancient city (vanilla ancient_city), deep under the mountains where the deep
 * dark is: a cavern hollowed out of deepslate around a plaza of deepslate
 * tiles, with the great "portal" frame of deepslate bricks and chiseled
 * deepslate at its heart, ruined houses along the sides, soul-lit lanterns
 * (soul torches here), and sculk creeping over everything.
 */

import { SEA_LEVEL } from '../../constants';
import { AIR, WOOL_IDS } from '../../blocks';
import { vy } from '../ctx';
import type { TerrainGenerator } from '../terrain';
import { block, type Builder, cell, chance, F, int, centredPiece, type Piece, type Rng, seedOf, type StructureType } from './kit';

const TILES = block('Deepslate Tiles');
const BRICKS = cell.mix([[block('Deepslate Bricks'), 4], [block('Polished Deepslate'), 1], [block('Cobbled Deepslate'), 1]]);
const CHISELED = block('Chiseled Deepslate');
const SCULK = block('Sculk');
const SOUL = block('Soul Torch');
const SIZE = 41;

function buildCity(b: Builder): void {
  const y = b.y, r = b.rnd;
  // the cavern, and the plaza floor
  for (let v = 0; v < SIZE; v++) {
    for (let u = 0; u < SIZE; u++) {
      const d = Math.hypot(u - 20, v - 20) / 20;
      if (d > 1) continue;
      const h = Math.round(14 * Math.sqrt(1 - d * d)) + 3;
      b.fill(u, y + 1, v, u, y + h, v, AIR);
      b.set(u, y, v, r() < 0.25 ? SCULK : TILES);
    }
  }
  // the portal frame: two towers joined by an arch, its opening dark
  b.fill(12, y + 1, 18, 28, y + 14, 22, BRICKS);
  b.fill(15, y + 1, 18, 25, y + 10, 22, AIR);
  b.fill(12, y + 14, 18, 28, y + 14, 22, CHISELED);
  for (const u of [12, 28]) b.fill(u, y + 1, 20, u, y + 13, 20, CHISELED);
  b.fill(16, y + 1, 19, 24, y + 1, 21, block('Deepslate Tiles'));
  b.fill(17, y + 2, 20, 23, y + 9, 20, block('Basalt'));
  b.fill(18, y + 3, 20, 22, y + 8, 20, AIR);
  // ruined houses along the sides of the plaza, wool carpets inside
  for (const [u0, v0] of [[4, 6], [30, 6], [4, 28], [30, 28], [17, 4], [17, 32]]) {
    b.room(u0, y, v0, u0 + 6, y + 4, v0 + 5, BRICKS);
    b.fill(u0 + 1, y + 4, v0 + 1, u0 + 5, y + 4, v0 + 4, AIR);  // the roofs fell in
    b.fill(u0 + 3, y + 1, v0, u0 + 3, y + 2, v0, AIR);
    b.fill(u0 + 1, y, v0 + 1, u0 + 5, y, v0 + 4, chance(r, 0.5) ? WOOL_IDS.gray : WOOL_IDS.light_blue);
    if (chance(r, 0.6)) b.chest(u0 + 1 + int(r, 5), y + 1, v0 + 4, F.NV);
    b.set(u0 + 5, y + 1, v0 + 1, SOUL);
  }
  // lamp posts round the plaza, and sculk creeping up the walls
  for (let a = 0; a < 8; a++) {
    const u = Math.round(20 + 11 * Math.cos((a * Math.PI) / 4)), v = Math.round(20 + 11 * Math.sin((a * Math.PI) / 4));
    b.fill(u, y + 1, v, u, y + 2, v, BRICKS);
    b.set(u, y + 3, v, SOUL);
  }
  for (let i = 0; i < 160; i++) {
    const u = int(r, SIZE), v = int(r, SIZE), dy = int(r, 6);
    const c = b.get(u, y + dy, v);
    if (c > 0) b.set(u, y + dy, v, SCULK);
  }
}

function ancientCity(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  // the deep dark lies under high ground; keep at least 20 blocks of rock above
  if (c.height < SEA_LEVEL + 20) return null;
  const y = vy(-51);
  if (c.height - y < 40) return null;
  return [centredPiece(x, z, int(rnd, 4), SIZE, SIZE, y, false, seedOf(rnd), buildCity)];
}

export const ANCIENT_CITY: StructureType = {
  name: 'Ancient City', spacing: 24, separation: 8, salt: 20083232, reach: 2, generate: ancientCity,
};
